import { useState, useEffect, useRef, DragEvent, ChangeEvent } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Eye,
  Download,
  FileText,
  Clock,
} from 'lucide-react';
import { barrattAPI, type ImportSummary, type ImportLog, type ImportException } from '../../lib/api/barratt';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Allowed file types for upload (must match backend ALLOWED_IMPORT_MIME_TYPES)
const ALLOWED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls', '.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg', '.gif'];
const ALLOWED_FILE_TYPES_MESSAGE = 'Allowed file types: CSV, XLSX, XLS, PDF, DOC, DOCX, PNG, JPG, JPEG, GIF';

// Validate file type before upload
const isValidFileType = (filename: string): boolean => {
  const ext = '.' + filename.split('.').pop()?.toLowerCase();
  return ALLOWED_FILE_EXTENSIONS.includes(ext);
};

// Barratt-specific import templates
const IMPORT_TEMPLATES = [
  {
    id: 1,
    name: 'Invoice',
    description: 'Invoice data for spend analysis',
    columns: ['Invoice_Number', 'Invoice_Date', 'Supplier_Code', 'Amount', 'Tax', 'Total'],
  },
  {
    id: 2,
    name: 'Supplier_Performance',
    description: 'Delivery performance data by supplier',
    columns: [
      'Barratt_PO_Number', 'Supplier_Internal_Reference', 'Site_Plot_Number',
      'BDW_Divisional_Reference', 'Purchase_Order_Date', 'Date_of_Call_Off',
      'Required_Date', 'Date_of_Agreed_Delivery', 'Current_Promise_Date',
      'SLA_Delivery_Date', 'Actual_Date_of_Delivery', 'Days_of_Early_Allowed',
      'Days_of_Late_Allowed', 'Order_Value_line_item', 'Reason_Code',
    ],
  },
  {
    id: 3,
    name: 'Division_Performance',
    description: 'Quarterly division performance scores',
    columns: ['Supplier_Code', 'Division', 'Quarter', 'Year', 'Score', 'Comments'],
  },
  {
    id: 4,
    name: 'Quality',
    description: 'Quality metrics and NCR data',
    columns: ['NCR_Number', 'Supplier_Code', 'Issue_Date', 'Category', 'Severity', 'Description'],
  },
];

// Status badge component - shows Processing, Complete, Completed with Errors, or Failed
interface StatusBadgeProps {
  isComplete: boolean;
  isSuccess: boolean;
  hasErrors?: boolean;
  totalAdded?: number;
  totalFailed?: number;
}

const StatusBadge = ({ isComplete, isSuccess, hasErrors, totalAdded, totalFailed: _totalFailed }: StatusBadgeProps) => {
  if (!isComplete) {
    return (
      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
        <Clock className="h-3 w-3 mr-1" />
        Processing
      </Badge>
    );
  }

  // Completed with Errors: some rows imported, some failed
  if (hasErrors && totalAdded && totalAdded > 0) {
    return (
      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Completed with Errors
      </Badge>
    );
  }

  if (isSuccess) {
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Complete
      </Badge>
    );
  }

  // All rows failed
  return (
    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
      <XCircle className="h-3 w-3 mr-1" />
      Failed
    </Badge>
  );
};

export default function DataUpload() {
  const confirm = useConfirm();
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentImport, setCurrentImport] = useState<ImportSummary | null>(null);
  const [importHistory, setImportHistory] = useState<ImportSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedImport, setSelectedImport] = useState<ImportSummary | null>(null);
  const [importLogs, setImportLogs] = useState<ImportLog[]>([]);
  const [importExceptions, setImportExceptions] = useState<ImportException[]>([]);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch import history
  const fetchImportHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await barrattAPI.getImportSummaries({ page_size: 20 });
      setImportHistory(response.data.results || []);
    } catch (error) {
      console.error('Error fetching import history:', error);
      toast.error('Failed to load import history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchImportHistory();
  }, []);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    if (!selectedTemplate) {
      toast.error('Please select a template before uploading');
      return;
    }

    // Client-side file type validation
    if (!isValidFileType(file.name)) {
      toast.error(`Invalid file type. ${ALLOWED_FILE_TYPES_MESSAGE}`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_id', selectedTemplate);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress during upload
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 300);

      const response = await barrattAPI.uploadFile(formData);
      clearInterval(progressInterval);
      setUploadProgress(100);

      setCurrentImport(response.data);
      toast.success('File uploaded successfully');

      // Refresh history after upload
      await fetchImportHistory();
    } catch (error: unknown) {
      console.error('Error uploading file:', error);
      // Extract error message from API response
      let errorMessage = 'Failed to upload file';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  // Handle drag events
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedTemplate) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (!selectedTemplate || isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (isValidFileType(file.name)) {
        handleFileUpload(file);
      } else {
        toast.error(`Invalid file type. ${ALLOWED_FILE_TYPES_MESSAGE}`);
      }
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (isValidFileType(file.name)) {
        handleFileUpload(file);
      } else {
        toast.error(`Invalid file type. ${ALLOWED_FILE_TYPES_MESSAGE}`);
      }
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // View import details
  const handleViewDetails = async (importSummary: ImportSummary) => {
    setSelectedImport(importSummary);
    setIsDetailsOpen(true);

    try {
      const [logsRes, exceptionsRes] = await Promise.all([
        barrattAPI.getImportSummaryLogs(importSummary.id),
        barrattAPI.getImportSummaryExceptions(importSummary.id),
      ]);
      setImportLogs(logsRes.data);
      setImportExceptions(exceptionsRes.data);
    } catch (error) {
      console.error('Error fetching import details:', error);
      toast.error('Failed to load import details');
    }
  };

  // Delete import
  const handleDeleteImport = async (importId: number) => {
    const ok = await confirm({ title: 'Delete Import', description: 'Are you sure you want to delete this import record?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;

    try {
      await barrattAPI.deleteImportSummary(importId);
      toast.success('Import record deleted');
      await fetchImportHistory();
    } catch (error) {
      console.error('Error deleting import:', error);
      toast.error('Failed to delete import record');
    }
  };

  // Get template by ID
  const getTemplateById = (id: number) => {
    return IMPORT_TEMPLATES.find((t) => t.id === id);
  };

  // Download template
  const handleDownloadTemplate = () => {
    if (!selectedTemplate) return;
    const template = IMPORT_TEMPLATES.find((t) => t.id.toString() === selectedTemplate);
    if (!template) return;

    const csvContent = template.columns.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.name}_template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template downloaded');
  };

  const selectedTemplateData = IMPORT_TEMPLATES.find((t) => t.id.toString() === selectedTemplate);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Data Upload</h1>
          <p className="text-muted-foreground">
            Upload data files using Barratt-specific templates
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchImportHistory} disabled={isLoadingHistory}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingHistory ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Template Selection & Upload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Template Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Select Template
            </CardTitle>
            <CardDescription>
              Choose the data template that matches your file format
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                {IMPORT_TEMPLATES.map((template) => (
                  <SelectItem key={template.id} value={template.id.toString()}>
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      <span>{template.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedTemplateData && (
              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">{selectedTemplateData.description}</p>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Expected Columns:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedTemplateData.columns.slice(0, 8).map((col) => (
                      <Badge key={col} variant="secondary" className="text-xs">
                        {col}
                      </Badge>
                    ))}
                    {selectedTemplateData.columns.length > 8 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedTemplateData.columns.length - 8} more
                      </Badge>
                    )}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload File
            </CardTitle>
            <CardDescription>
              Drag and drop or click to select a file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.pdf,.doc,.docx,.png,.jpg,.jpeg,.gif"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={isUploading || !selectedTemplate}
            />
            <div
              onClick={() => selectedTemplate && !isUploading && fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
                ${!selectedTemplate ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary hover:bg-primary/5'}
              `}
            >
              <Upload className={`h-12 w-12 mx-auto mb-4 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
              {!selectedTemplate ? (
                <p className="text-muted-foreground">Select a template first</p>
              ) : isDragActive ? (
                <p className="text-primary font-medium">Drop the file here...</p>
              ) : (
                <div>
                  <p className="text-muted-foreground mb-2">
                    Drag and drop a file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supported formats: CSV, XLSX, XLS, PDF, DOC, DOCX, PNG, JPG, JPEG, GIF
                  </p>
                </div>
              )}
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Uploading...</span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}

            {/* Current Import Status */}
            {currentImport && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Import #{currentImport.id}</span>
                  <StatusBadge
                    isComplete={currentImport.is_complete}
                    isSuccess={currentImport.is_success}
                    hasErrors={currentImport.has_errors}
                    totalAdded={currentImport.total_added}
                    totalFailed={currentImport.total_failed}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Records:</span>
                    <span className="ml-2 font-medium">{currentImport.total_records}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pages:</span>
                    <span className="ml-2 font-medium">
                      {currentImport.imported_pages}/{currentImport.total_pages}
                    </span>
                  </div>
                </div>
                {currentImport.progress_percentage !== undefined && (
                  <Progress value={currentImport.progress_percentage} />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Import History */}
      <Card>
        <CardHeader>
          <CardTitle>Import History</CardTitle>
          <CardDescription>
            Recent data imports and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : importHistory.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No imports yet. Upload a file to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Records</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importHistory.map((imp) => {
                  const template = imp.template ? getTemplateById(imp.template) : null;
                  return (
                    <TableRow key={imp.id}>
                      <TableCell className="font-medium">#{imp.id}</TableCell>
                      <TableCell>
                        {template?.name || imp.dataset_name || 'Unknown'}
                      </TableCell>
                      <TableCell>{imp.total_records.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={(imp.imported_pages / Math.max(imp.total_pages, 1)) * 100}
                            className="w-20 h-2"
                          />
                          <span className="text-xs text-muted-foreground">
                            {imp.imported_pages}/{imp.total_pages}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          isComplete={imp.is_complete}
                          isSuccess={imp.is_success}
                          hasErrors={imp.has_errors}
                          totalAdded={imp.total_added}
                          totalFailed={imp.total_failed}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(imp.created_at), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(imp)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteImport(imp.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Import Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Details #{selectedImport?.id}</DialogTitle>
            <DialogDescription>
              View import logs and exceptions
            </DialogDescription>
          </DialogHeader>

          {selectedImport && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Total Records</p>
                  <p className="text-lg font-semibold">{selectedImport.total_records}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Added</p>
                  <p className="text-lg font-semibold text-green-600">
                    {selectedImport.total_added ?? 0}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Failed</p>
                  <p className="text-lg font-semibold text-red-600">
                    {selectedImport.total_failed ?? 0}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Pages</p>
                  <p className="text-lg font-semibold">
                    {selectedImport.imported_pages}/{selectedImport.total_pages}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <StatusBadge
                    isComplete={selectedImport.is_complete}
                    isSuccess={selectedImport.is_success}
                    hasErrors={selectedImport.has_errors}
                    totalAdded={selectedImport.total_added}
                    totalFailed={selectedImport.total_failed}
                  />
                </div>
              </div>

              {/* Import Logs */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Processing Logs
                </h4>
                {importLogs.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No logs available</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Page</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Added</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead>Skipped</TableHead>
                        <TableHead>Failed</TableHead>
                        <TableHead>Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>Page {log.page_index + 1}</TableCell>
                          <TableCell>
                            {log.is_success ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                          </TableCell>
                          <TableCell className="text-green-600">{log.added}</TableCell>
                          <TableCell className="text-blue-600">{log.updated}</TableCell>
                          <TableCell className="text-yellow-600">{log.skipped}</TableCell>
                          <TableCell className="text-red-600">{log.failed}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {log.finish_time
                              ? format(new Date(log.finish_time), 'HH:mm:ss')
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Exceptions */}
              {importExceptions.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    Exceptions ({importExceptions.length})
                  </h4>
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Row</TableHead>
                          <TableHead>Column</TableHead>
                          <TableHead>Error Code</TableHead>
                          <TableHead>Severity</TableHead>
                          <TableHead>Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importExceptions.map((exc) => (
                          <TableRow key={exc.id}>
                            <TableCell className="font-mono">
                              {exc.row_number === 0 ? 'Header' : exc.row_number}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {exc.column_name || '-'}
                            </TableCell>
                            <TableCell>
                              {exc.error_code && (
                                <Badge variant="secondary" className="font-mono text-xs">
                                  {exc.error_code}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  exc.severity === 'error'
                                    ? 'bg-red-50 text-red-700 border-red-200'
                                    : exc.severity === 'warning'
                                    ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                    : 'bg-blue-50 text-blue-700 border-blue-200'
                                }
                              >
                                {exc.severity}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[300px]">
                              <span className="line-clamp-2">{exc.message}</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
